import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const HERO_IMAGE = "https://images.pexels.com/photos/159306/construction-site-build-construction-work-159306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setErrors(getFieldErrors(err));
      setFormError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      {/* Full-screen background */}
      <img src={HERO_IMAGE} alt="Construction site" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-slate-950/75" />

      {/* Theme toggle top-right */}
      <ThemeToggle className="absolute top-6 right-6 z-10 border-white/20 text-white hover:border-white/40 hover:text-white" />

      {/* Centered form card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="rounded-2xl border border-white/10 bg-white/95 dark:bg-[#151C2D]/95 backdrop-blur-md shadow-2xl px-8 py-10">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <BrandMark variant="light" />
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground text-center">Sign in</h2>
          <p className="mb-8 mt-1 text-sm text-muted-foreground text-center">Access your project command center</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="mt-2"
              />
              <FieldError error={errors.email} />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  title={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <FieldError error={errors.password} />
            </div>

            {formError && <p className="text-xs text-red-600 dark:text-red-400" data-testid="login-error-message">{formError}</p>}

            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full uppercase tracking-[0.15em]">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
        </div>

        {/* Tagline below card */}
        <p className="mt-6 text-center text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
          buildnexa.io · ERP Platform
        </p>
      </div>
    </div>
  );
}
